import { useState } from 'react';
import { chatAPI } from '../services/api';

export const useChatSession = () => {
  const [sessionId, setSessionId] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [userInfo, setUserInfo] = useState({
    name: null,
    email: null
  });
  const [rateLimits, setRateLimits] = useState({
    dailyRemaining: 80,
    dailyLimit: 80
  });
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Check if session is still valid (1 hour timeout)
  const isSessionValid = () => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) return false;
    
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return new Date(lastActivity) > oneHourAgo;
  };

  // Update activity on each message
  const updateActivity = () => {
    localStorage.setItem('lastActivity', new Date().toISOString());
  };

  // Check for warnings based on daily rate limits only
  const checkWarnings = (dailyRemaining) => {
    setWarning(null);
    
    if (dailyRemaining <= 5 && dailyRemaining > 0) {
      setWarning({
        type: 'daily',
        message: `Only ${dailyRemaining} messages left today. Please come back tomorrow for more conversations.`,
        icon: 'HiExclamationCircle'
      });
    }
  };

  // Get personalized greeting based on user info
  const getPersonalizedGreeting = () => {
    if (userInfo.name) {
      return `Hey ${userInfo.name}! 👋 You're back — love it. What do you want to talk about this time?`;
    }
    return "Hey! 👋 I'm Lovish. You can ask me about my work, projects, tech stack, career journey — pretty much anything. I'd also love to know who I'm talking to — what's your name and what brings you here?";
  };

  // Initialize chat session
  const initializeChat = async () => {
    try {
      setIsInitializing(true);
      setError(null);
      setWarning(null);
      
      // Check for existing user token
      let existingToken = localStorage.getItem('userToken');
      
      // Check if session is still valid
      if (existingToken && !isSessionValid()) {
        // Session expired, clear old session data
        localStorage.removeItem('sessionId');
        localStorage.removeItem('lastActivity');
      }
      
      const { data, headers } = await chatAPI.initSession(existingToken);
      
      setSessionId(data.sessionId);
      setUserToken(data.userToken);
      
      // Store the token and session data
      localStorage.setItem('userToken', data.userToken);
      localStorage.setItem('sessionId', data.sessionId);
      updateActivity();
      
      // Update user info if provided
      if (data.userInfo) {
        setUserInfo({
          name: data.userInfo.name || null,
          email: data.userInfo.email || null
        });
      }
      
      // Update rate limits - only daily limits now
      setRateLimits({
        dailyRemaining: parseInt(headers.dailyRemaining) || 80,
        dailyLimit: parseInt(headers.dailyLimit) || 80
      });

      // Check for warnings - only daily warnings
      checkWarnings(parseInt(headers.dailyRemaining) || 80);

      return getPersonalizedGreeting();

    } catch (error) {
      console.error('Failed to initialize chat:', error);
      setError('Failed to connect to chat service. Please try refreshing the page.');
      return getPersonalizedGreeting();
    } finally {
      setIsInitializing(false);
    }
  };

  // Update user info and handle celebration trigger
  const updateUserInfo = (newUserInfo, onNameCaptured) => {
    setUserInfo(prev => {
      const updatedInfo = {
        name: newUserInfo.name || prev.name,
        email: newUserInfo.email || prev.email
      };
      
      // Trigger celebration if name changed
      if (newUserInfo.name !== prev.name) {
        onNameCaptured && onNameCaptured();
      }
      
      return updatedInfo;
    });
  };

  // Update rate limits
  const updateRateLimits = (headers) => {
    const newDailyRemaining = parseInt(headers.dailyRemaining) || rateLimits.dailyRemaining;
    
    setRateLimits({
      dailyRemaining: newDailyRemaining,
      dailyLimit: parseInt(headers.dailyLimit) || rateLimits.dailyLimit
    });

    checkWarnings(newDailyRemaining);
  };

  return {
    // State
    sessionId,
    userToken,
    userInfo,
    rateLimits,
    error,
    warning,
    isInitializing,
    
    // Actions
    initializeChat,
    updateActivity,
    updateUserInfo,
    updateRateLimits,
    setError,
    setWarning,
    getPersonalizedGreeting
  };
}; 